import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment-timezone";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import GavelIcon from "@mui/icons-material/Gavel";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from "../../config/api";
import { usePageStyles } from "../common/pageStyles";

const BASE_URL = API_BASE_URL;

const useStyles = makeStyles((theme) => ({
  content: { width: "100%", minWidth: 0 },
  paper: {
    padding: theme.spacing(3),
    width: "100%",
    maxWidth: "1200px",
    boxSizing: "border-box",
    margin: "0 auto",
  },
  pageTitle: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
    fontWeight: 700,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 700,
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  editCard: {
    cursor: "pointer",
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  activeCard: {
    border: "2px solid #1976d2",
    boxShadow: theme.shadows[5],
  },
  cardLabel: {
    fontSize: "0.85rem",
    color: "#666",
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontWeight: 600,
    wordBreak: "break-word",
  },
  editorBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: "#f7fbff",
    border: "1px solid #d7e8ff",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  summaryItem: {
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "#f8f8f8",
    border: "1px solid #e6e6e6",
  },
  summaryLabel: {
    fontSize: "0.8rem",
    color: "#666",
    marginBottom: theme.spacing(0.5),
  },
  commentsBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: "#fbfbfb",
    border: "1px solid #e2e8f0",
  },
  mailNotificationButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  mailNotificationComment: {
    backgroundColor: "#fff8e1",
    borderLeft: "4px solid #ff9800",
  },
  mailNotificationBadge: {
    display: "inline-block",
    marginBottom: theme.spacing(0.5),
    padding: "2px 8px",
    borderRadius: 12,
    backgroundColor: "#ff9800",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.2px",
  },
  commentsList: {
    display: "grid",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  commentCard: {
    padding: theme.spacing(2),
    borderRadius: 10,
    border: "1px solid #e0e0e0",
    backgroundColor: "#fff",
  },
  commentMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  actionsRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    flexWrap: "wrap",
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  statusWniosekExists: {
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    "& .MuiTypography-root": {
      color: "#155724",
      fontWeight: 600,
    },
  },
  statusWniosekNotExists: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    "& .MuiTypography-root": {
      color: "#721c24",
      fontWeight: 600,
    },
  },
  statusWniosekChecking: {
    backgroundColor: "#fff3cd",
    borderColor: "#ffeaa7",
    "& .MuiTypography-root": {
      color: "#856404",
      fontWeight: 600,
    },
  },
  statusWniosekArchive: {
    backgroundColor: "#e2e3e5",
    borderColor: "#d3d6db",
    "& .MuiTypography-root": {
      color: "#383d41",
      fontWeight: 600,
    },
  },
  lockedCard: {
    opacity: 0.45,
    backgroundColor: "#f5f5f5",
    pointerEvents: "none",
    cursor: "not-allowed",
  },
}));

const initialFormData = {
  powodWpisania: "",
  dataSAD: "",
  mrn: "",
  nrAkt: "",
  dsNrRef: "",
  nadawca: "",
  odbiorca: "",
  podpisany: "",
  rodzajSAD: "",
  procedura: "",
  kontrolujacy: "",
  kwalifikujeDoKorekty: "",
  nieprawidlowosci: "",
  rodzajBledu: "",
  wniosekWRw: null,
  sciezkaDoPliku: "",
  dataKontroliWewnetrznej: "",
  dataUmieszczeniaWRejestrzeDs: "",
  dataZgloszeniaDoDias: "",
  status: "",
  duplikat: 0,
  createdBy: "",
  createdAt: "",
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const isValidDate = (value) => {
  return /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/.test(value);
};

const MIN_MRN_PREFIX_YEAR = 23;

const hasAllowedMrnPrefix = (normalizedValue) => {
  const match = String(normalizedValue || "").match(/^(\d{2})PL/);

  if (!match) {
    return false;
  }

  return Number(match[1]) >= MIN_MRN_PREFIX_YEAR;
};

const normalizeMrnValue = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 20);

const isValidMrn = (value) => {
  const normalizedValue = normalizeMrnValue(String(value || "").trim());
  return (
    hasAllowedMrnPrefix(normalizedValue) &&
    /^[A-Z0-9]{18,20}$/.test(normalizedValue)
  );
};

const isValidNrAkt = (value) => {
  const trimmedValue = String(value || "").trim();
  return trimmedValue === "" || /^[^/\s]+\/[^/\s]+$/.test(trimmedValue);
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("pl-PL", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}.${month}.${year}`;
  }

  if (/^\d{2}\.\d{2}\.\d{4}$/.test(text)) {
    return text;
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return text;
  }

  return date.toLocaleDateString("pl-PL", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const formatCommentDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("pl-PL", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getLoggedUsername = () => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      return "";
    }

    return String(jwtDecode(token)?.username || "").trim();
  } catch (error) {
    return "";
  }
};

const normalizeRecipients = (values = []) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => String(value || "").split(","))
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );

const filterOptions = createFilterOptions();

const editableFields = [
  { key: "powodWpisania", label: "Powód wpisania", type: "select-list" },
  { key: "dataSAD", label: "Data SAD", type: "text" },
  { key: "mrn", label: "MRN", type: "text" },
  { key: "nrAkt", label: "Nr akt", type: "text" },
  { key: "sciezkaDoPliku", label: "Ścieżka do plików", type: "text" },
  { key: "nadawca", label: "Nadawca", type: "text" },
  { key: "odbiorca", label: "Odbiorca", type: "text" },
  { key: "podpisany", label: "Podpisany", type: "users" },
  { key: "rodzajSAD", label: "Rodzaj SAD", type: "select-list" },
  { key: "procedura", label: "Procedura", type: "select-list" },
  { key: "kontrolujacy", label: "Kontrolujący", type: "users" },
  { key: "nieprawidlowosci", label: "Nieprawidłowości", type: "tak-nie" },
  {
    key: "kwalifikujeDoKorekty",
    label: "Czy kwalifikuje się do korekty",
    type: "yes-no",
  },
  { key: "rodzajBledu", label: "Rodzaj błędu", type: "select-list" },
];

const importTypes = [
  "Import",
  "Import - wpis do rejestru",
  "Wprowadzenie na skład",
  "Wprowadzenie na magazyn",
  "Uszlachetnianie czynne",
  "Powrotny przywóz",
  "Odprawa czasowa import",
];

const exportTypes = [
  "Eksport",
  "Wyprowadzenie ze składu",
  "Wyprowadzenie z magazynu",
  "Uszlachetnianie bierne",
  "Powrotny wywóz",
  "Eksport - wpis do rejestru",
  "Eksport PL i import inny kraj",
  "Eksport PL + import",
  "Odprawa czasowa eksport",
];

const getChecklistRoute = (rodzaj, nrRef, entryId) => {
  if (!nrRef || !entryId) {
    return null;
  }

  if (importTypes.includes(rodzaj)) {
    return `/UniwersalListImport/${nrRef}/${entryId}`;
  }

  if (exportTypes.includes(rodzaj)) {
    return `/UniwersalListEksport/${nrRef}/${entryId}`;
  }

  if (rodzaj === "Tranzyt") {
    return `/UniwersalListTranzyt/${nrRef}/${entryId}`;
  }

  return null;
};

const KWDetail = () => {
  const classes = useStyles();
  const pageClasses = usePageStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeField, setActiveField] = useState("powodWpisania");
  const [formData, setFormData] = useState(initialFormData);
  const [uzytkownicyList, setUzytkownicyList] = useState([]);
  const [powodyWpisaniaList, setPowodyWpisaniaList] = useState([]);
  const [rodzajeSADList, setRodzajeSADList] = useState([]);
  const [proceduryList, setProceduryList] = useState([]);
  const [rodzajeBleduList, setRodzajeBleduList] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const [isRedirectingToChecklist, setIsRedirectingToChecklist] =
    useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState([]);
  const [recipientsInputValue, setRecipientsInputValue] = useState("");
  const [recipientsError, setRecipientsError] = useState("");
  const [validLoginsList, setValidLoginsList] = useState([]);
  const [isChecklistDone, setIsChecklistDone] = useState(false);
  const [checklistLink, setChecklistLink] = useState("");
  const mailNotificationPrefix = "[POWIADOMIENIE_MAILOWE]";

  const loggedUsername = getLoggedUsername();

  const hasInvalidDataSAD =
    formData.dataSAD.trim() !== "" && !isValidDate(formData.dataSAD.trim());
  const hasInvalidMRN =
    formData.mrn.trim() !== "" && !isValidMrn(formData.mrn.trim());
  const hasInvalidNrAkt =
    (formData.nrAkt?.trim() || "") !== "" &&
    !isValidNrAkt(formData.nrAkt?.trim() || "");

  const isCommentingBlocked = formData.kwalifikujeDoKorekty === "TAK";

  const loadEntryDetails = useCallback(async () => {
    const response = await axios.get(`${BASE_URL}/kw-dane/${id}`, {
      headers: getAuthHeaders(),
    });

    setFormData({
      ...initialFormData,
      ...response.data,
      mrn: normalizeMrnValue(response.data?.mrn || ""),
    });
  }, [id]);

  const loadComments = useCallback(async () => {
    const response = await axios.get(`${BASE_URL}/kw-comments/${id}`, {
      headers: getAuthHeaders(),
    });

    setComments(Array.isArray(response.data) ? response.data : []);
  }, [id]);

  const isMailNotificationComment = (commentText = "") =>
    String(commentText).startsWith(mailNotificationPrefix);

  const getDisplayedCommentText = (commentText = "") => {
    if (!isMailNotificationComment(commentText)) {
      return commentText;
    }

    return String(commentText).replace(mailNotificationPrefix, "").trim();
  };

  useEffect(() => {
    const headers = getAuthHeaders();

    const mapApiDataToSortedNames = (data) =>
      Array.isArray(data)
        ? Array.from(
            new Set(data.map((item) => item?.name?.trim()).filter(Boolean)),
          ).sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }))
        : [];

    const loadPageData = async () => {
      setIsLoading(true);

      try {
        const [
          usersResponse,
          loginsResponse,
          powodyResponse,
          rodzajeResponse,
          proceduryResponse,
          rodzajeBleduResponse,
        ] = await Promise.all([
          axios.get(`${BASE_URL}/list/uzytkownicy`, { headers }),
          axios.get(`${BASE_URL}/users`, { headers }),
          axios.get(`${BASE_URL}/list/kwPowodyWpisania`, { headers }),
          axios.get(`${BASE_URL}/list/kwRodzajSad`, { headers }),
          axios.get(`${BASE_URL}/list/kwProcedura`, { headers }),
          axios.get(`${BASE_URL}/list/kwRodzajBledu`, { headers }),
        ]);

        const sortedUsers = Array.isArray(usersResponse.data)
          ? [...usersResponse.data].sort((a, b) =>
              (a?.name || "").localeCompare(b?.name || "", "pl", {
                sensitivity: "base",
              }),
            )
          : [];

        setUzytkownicyList(sortedUsers);
        setValidLoginsList(
          Array.isArray(loginsResponse.data)
            ? loginsResponse.data
                .map((item) => String(item?.username || "").trim())
                .filter(Boolean)
                .sort((a, b) =>
                  a.localeCompare(b, "pl", { sensitivity: "base" }),
                )
            : [],
        );
        setPowodyWpisaniaList(mapApiDataToSortedNames(powodyResponse.data));
        setRodzajeSADList(mapApiDataToSortedNames(rodzajeResponse.data));
        setProceduryList(mapApiDataToSortedNames(proceduryResponse.data));
        setRodzajeBleduList(mapApiDataToSortedNames(rodzajeBleduResponse.data));

        await Promise.all([loadEntryDetails(), loadComments()]);

        try {
          const statusRes = await axios.get(
            `${BASE_URL}/kw-dane/${id}/checklist-status`,
            { headers },
          );
          if (statusRes.data?.isInternalControlComplete) {
            setIsChecklistDone(true);
          }
        } catch (_) {}
      } catch (error) {
        console.error("Błąd podczas pobierania danych wpisu KW:", error);
        toast.error(
          error.response?.data?.message ||
            "Nie udało się pobrać szczegółów wpisu KW.",
        );
        navigate("/kontrolaWewnetrzna");
      } finally {
        setIsLoading(false);
      }
    };

    loadPageData();
  }, [loadComments, loadEntryDetails, navigate, id]);

  useEffect(() => {
    const checkChecklistStatus = async () => {
      const hasChecklistReturnFlag =
        sessionStorage.getItem(`kw-checklist-return-${id}`) === "1";

      if (
        (location.state?.fromChecklist || hasChecklistReturnFlag) &&
        !isLoading &&
        id
      ) {
        try {
          const response = await axios.get(
            `${BASE_URL}/kw-dane/${id}/checklist-status`,
            { headers: getAuthHeaders() },
          );
          if (response.data?.isInternalControlComplete) {
            setIsChecklistDone(true);
          }
        } catch (error) {
          console.error(
            "Błąd podczas sprawdzania statusu kontroli wewnętrznej:",
            error,
          );
        } finally {
          sessionStorage.removeItem(`kw-checklist-return-${id}`);
        }
      }
    };

    checkChecklistStatus();
  }, [location.state, isLoading, id]);

  useEffect(() => {
    let isMounted = true;

    const loadChecklistLink = async () => {
      const normalizedMrn = normalizeMrnValue(formData.mrn);
      const normalizedNrAkt = formData.nrAkt?.trim() || "";

      if (
        !id ||
        !normalizedMrn ||
        !normalizedNrAkt ||
        !isValidMrn(normalizedMrn)
      ) {
        if (isMounted) {
          setChecklistLink("");
        }
        return;
      }

      try {
        const response = await axios.get(
          `${BASE_URL}/kw-dane/${id}/related-dane`,
          {
            headers: getAuthHeaders(),
          },
        );

        const relatedEntry = response.data?.data;
        const targetRoute = getChecklistRoute(
          relatedEntry?.rodzaj,
          relatedEntry?.nrRef,
          relatedEntry?.id,
        );

        if (!targetRoute) {
          if (isMounted) {
            setChecklistLink("");
          }
          return;
        }

        const searchParams = new URLSearchParams({
          kwDaneId: String(id),
          returnPath: `/kontrolaWewnetrzna/szczegoly/${id}`,
          returnLabel: "Powrót do KW",
          nrRef: String(relatedEntry?.nrRef || ""),
          osobaPrzygotowujaca: String(
            relatedEntry?.osobaPrzygotowujacaOdprawe || "",
          ),
          osobaWeryfikujaca: String(
            relatedEntry?.osobaWeryfikujacaOdprawe || "",
          ),
          osobaWysylajaca: String(relatedEntry?.osobaWysylajacaOdprawe || ""),
          status: String(relatedEntry?.statusRealizacji || ""),
          rodzajOdprawy: String(relatedEntry?.rodzaj || ""),
          lokalizacjaTowaru: String(relatedEntry?.lokalizacjaTowaru || ""),
        });

        if (isMounted) {
          setChecklistLink(`${targetRoute}?${searchParams.toString()}`);
        }
      } catch (_) {
        if (isMounted) {
          setChecklistLink("");
        }
      }
    };

    loadChecklistLink();

    return () => {
      isMounted = false;
    };
  }, [id, formData.mrn, formData.nrAkt]);

  const handleGoToChecklist = async () => {
    const normalizedMrn = normalizeMrnValue(formData.mrn);
    const normalizedNrAkt = formData.nrAkt?.trim() || "";

    if (!normalizedMrn) {
      toast.warning("Ten wpis KW nie ma jeszcze uzupełnionego numeru MRN.");
      return;
    }

    if (!normalizedNrAkt) {
      toast.warning("Ten wpis KW nie ma jeszcze uzupełnionego numeru akt.");
      return;
    }

    if (!isValidMrn(normalizedMrn)) {
      toast.warning(
        `MRN musi zaczynać się od XXPL, gdzie XX >= ${MIN_MRN_PREFIX_YEAR} (np. 23PL, 24PL) i mieć 18-20 znaków (tylko litery i cyfry).`,
      );
      return;
    }

    setIsRedirectingToChecklist(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/kw-dane/${id}/related-dane`,
        {
          headers: getAuthHeaders(),
        },
      );

      const relatedEntry = response.data?.data;
      const targetRoute = getChecklistRoute(
        relatedEntry?.rodzaj,
        relatedEntry?.nrRef,
        relatedEntry?.id,
      );

      if (!targetRoute) {
        toast.warning(
          "Znaleziono odprawę, ale jej rodzaj nie obsługuje jeszcze Listy kontrolnej.",
        );
        return;
      }

      sessionStorage.setItem(`kw-checklist-return-${id}`, "1");

      navigate(targetRoute, {
        state: {
          nrRef: relatedEntry.nrRef,
          osobaPrzygotowujaca: relatedEntry.osobaPrzygotowujacaOdprawe,
          osobaWeryfikujaca: relatedEntry.osobaWeryfikujacaOdprawe,
          osobaWysylajaca: relatedEntry.osobaWysylajacaOdprawe,
          status: relatedEntry.statusRealizacji,
          rodzajOdprawy: relatedEntry.rodzaj,
          lokalizacjaTowaru: relatedEntry.lokalizacjaTowaru,
          kwDaneId: id,
          returnPath: `/kontrolaWewnetrzna/szczegoly/${id}`,
          returnLabel: "Powrót do KW",
        },
      });
    } catch (error) {
      toast.warning(
        error.response?.data?.message ||
          "Nie znaleziono powiązanej odprawy dla tego wpisu KW.",
      );
    } finally {
      setIsRedirectingToChecklist(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const normalizedValue = name === "mrn" ? normalizeMrnValue(value) : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: normalizedValue };

      if (
        name === "nieprawidlowosci" &&
        String(normalizedValue).trim().toUpperCase() === "NIE"
      ) {
        next.kwalifikujeDoKorekty = "";
        next.rodzajBledu = "";
      }

      if (name === "kwalifikujeDoKorekty" && !String(normalizedValue).trim()) {
        next.rodzajBledu = "";
      }

      return next;
    });
  };

  const buildEntry = (data) => ({
    powodWpisania: data.powodWpisania?.trim() || "",
    dataSAD: data.dataSAD?.trim() || "",
    mrn: normalizeMrnValue(data.mrn?.trim() || ""),
    nrAkt: data.nrAkt?.trim() || "",
    nadawca: data.nadawca?.trim() || "",
    odbiorca: data.odbiorca?.trim() || "",
    podpisany: data.podpisany?.trim() || "",
    rodzajSAD: data.rodzajSAD?.trim() || "",
    procedura: data.procedura?.trim() || "",
    kontrolujacy: data.kontrolujacy?.trim() || "",
    kwalifikujeDoKorekty: data.kwalifikujeDoKorekty?.trim() || "",
    nieprawidlowosci: data.nieprawidlowosci?.trim() || "",
    rodzajBledu: data.rodzajBledu?.trim() || "",
    sciezkaDoPliku: data.sciezkaDoPliku?.trim() || "",
  });

  const getFieldValue = (key) => formData[key] || "-";

  const validateRecipientsFormat = (values) => {
    const normalizedValues = normalizeRecipients(values);

    if (!normalizedValues.length) {
      setRecipientsError("");
      return true;
    }

    const invalidLogins = normalizedValues.filter(
      (login) => !validLoginsList.includes(login),
    );

    if (invalidLogins.length > 0) {
      setRecipientsError(
        invalidLogins.length === 1
          ? `Login "${invalidLogins[0]}" nie istnieje w bazie danych.`
          : `Loginy nie istnieją w bazie danych: ${invalidLogins.join(", ")}`,
      );
      return false;
    }

    setRecipientsError("");
    return true;
  };

  const handleRecipientsInputChange = (_, newInputValue, reason) => {
    if (newInputValue.includes(",")) {
      const mergedRecipients = normalizeRecipients([
        ...additionalRecipients,
        newInputValue,
      ]);
      setAdditionalRecipients(mergedRecipients);
      validateRecipientsFormat(mergedRecipients);
      setRecipientsInputValue("");
      return;
    }

    if (reason === "reset") {
      setRecipientsInputValue("");
      return;
    }

    setRecipientsInputValue(newInputValue);
  };

  const handleAdditionalRecipientsChange = (_, newValue) => {
    const normalizedValues = normalizeRecipients(newValue);
    setAdditionalRecipients(normalizedValues);
    validateRecipientsFormat(normalizedValues);
  };

  const isFieldLocked = (key) => {
    const hasNrAkt = Boolean((formData.nrAkt || "").trim());
    const canStartInternalControlPath = isChecklistDone || !hasNrAkt;
    const hasNieprawidlowosci = Boolean(
      String(formData.nieprawidlowosci || "").trim(),
    );
    const isNieprawidlowosciNo =
      String(formData.nieprawidlowosci || "")
        .trim()
        .toUpperCase() === "NIE";
    const hasKwalifikujeDoKorekty = Boolean(
      String(formData.kwalifikujeDoKorekty || "").trim(),
    );

    if (key === "nieprawidlowosci")
      return !canStartInternalControlPath && !formData.nieprawidlowosci;

    // Pole kwalifikacji jest dostępne po wybraniu dowolnej wartości nieprawidłowości.
    if (key === "kwalifikujeDoKorekty") {
      return !hasNieprawidlowosci;
    }

    // Gdy kwalifikujeDoKorekty jest puste, pole rodzajBledu jest niedostępne.
    if (key === "rodzajBledu") {
      return isNieprawidlowosciNo || !hasKwalifikujeDoKorekty;
    }

    return false;
  };

  const renderActiveEditor = () => {
    switch (activeField) {
      case "powodWpisania":
        return (
          <TextField
            select
            fullWidth
            required
            label="Powód wpisania"
            name="powodWpisania"
            value={formData.powodWpisania}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz powód wpisania
            </MenuItem>
            {powodyWpisaniaList.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        );
      case "dataSAD":
        return (
          <TextField
            fullWidth
            required
            label="Data SAD"
            name="dataSAD"
            placeholder="dd.mm.yyyy"
            value={formData.dataSAD}
            onChange={handleInputChange}
            error={hasInvalidDataSAD}
            helperText={
              hasInvalidDataSAD
                ? "Niepoprawny format daty. Użyj dd.mm.yyyy"
                : "Wymagany format: dd.mm.yyyy"
            }
          />
        );
      case "mrn":
        return (
          <TextField
            fullWidth
            required
            label="MRN"
            name="mrn"
            value={formData.mrn}
            onChange={handleInputChange}
            error={hasInvalidMRN}
            helperText={
              hasInvalidMRN
                ? `MRN musi zaczynać się od XXPL, gdzie XX >= ${MIN_MRN_PREFIX_YEAR} (np. 23PL, 24PL) i mieć 18-20 znaków bez spacji i znaków specjalnych.`
                : `Wymagany format: XXPL... (XX >= ${MIN_MRN_PREFIX_YEAR}, 18-20 znaków)`
            }
            inputProps={{ maxLength: 20 }}
          />
        );
      case "nrAkt":
        return (
          <TextField
            fullWidth
            label="Nr akt"
            name="nrAkt"
            value={formData.nrAkt}
            onChange={handleInputChange}
            error={hasInvalidNrAkt}
            helperText={
              hasInvalidNrAkt
                ? "Jeśli wpisujesz Nr akt, użyj formatu dane1/dane2"
                : "Pole opcjonalne. Format: dane1/dane2"
            }
          />
        );
      case "sciezkaDoPliku":
        return (
          <TextField
            fullWidth
            label="Ścieżka do plików"
            name="sciezkaDoPliku"
            value={formData.sciezkaDoPliku || ""}
            onChange={handleInputChange}
            placeholder="Np. \\serwer\\folder\\plik.pdf"
          />
        );
      case "nadawca":
        return (
          <TextField
            fullWidth
            required
            label="Nadawca"
            name="nadawca"
            value={formData.nadawca}
            onChange={handleInputChange}
          />
        );
      case "odbiorca":
        return (
          <TextField
            fullWidth
            required
            label="Odbiorca"
            name="odbiorca"
            value={formData.odbiorca}
            onChange={handleInputChange}
          />
        );
      case "podpisany":
      case "kontrolujacy": {
        const label =
          activeField === "podpisany" ? "Podpisany" : "Kontrolujący";
        return (
          <TextField
            select
            fullWidth
            required
            label={label}
            name={activeField}
            value={formData[activeField]}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz użytkownika
            </MenuItem>
            {uzytkownicyList.map((item) => (
              <MenuItem key={item.id} value={item.name}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        );
      }
      case "rodzajSAD":
        return (
          <TextField
            select
            fullWidth
            required
            label="Rodzaj SAD"
            name="rodzajSAD"
            value={formData.rodzajSAD}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz rodzaj SAD
            </MenuItem>
            {rodzajeSADList.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        );
      case "procedura":
        return (
          <TextField
            select
            fullWidth
            required
            label="Procedura"
            name="procedura"
            value={formData.procedura}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz procedurę
            </MenuItem>
            {proceduryList.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        );
      case "nieprawidlowosci":
        return (
          <TextField
            select
            fullWidth
            label="Nieprawidłowości"
            name="nieprawidlowosci"
            value={formData.nieprawidlowosci || ""}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz
            </MenuItem>
            <MenuItem value="TAK">TAK</MenuItem>
            <MenuItem value="NIE">NIE</MenuItem>
          </TextField>
        );
      case "kwalifikujeDoKorekty":
        return (
          <TextField
            select
            fullWidth
            label="Czy kwalifikuje się do korekty"
            name="kwalifikujeDoKorekty"
            value={formData.kwalifikujeDoKorekty || ""}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz
            </MenuItem>
            <MenuItem value="TAK">TAK</MenuItem>
            <MenuItem value="NIE">NIE</MenuItem>
            <MenuItem value="DO OMÓWIENIA">DO OMÓWIENIA</MenuItem>
          </TextField>
        );
      case "rodzajBledu":
        return (
          <TextField
            select
            fullWidth
            label="Rodzaj błędu"
            name="rodzajBledu"
            value={formData.rodzajBledu || ""}
            onChange={handleInputChange}
          >
            <MenuItem value="" style={{ fontWeight: "bold" }}>
              Wybierz rodzaj błędu
            </MenuItem>
            {rodzajeBleduList.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        );
      default:
        return null;
    }
  };

  const handleSaveComment = async () => {
    if (!commentText.trim()) {
      toast.warning("Komentarz nie może być pusty.");
      return;
    }

    if (
      additionalRecipients.length > 0 &&
      !validateRecipientsFormat(additionalRecipients)
    ) {
      toast.warning("Popraw loginy dodatkowych odbiorców powiadomienia.");
      return;
    }

    const defaultRecipients = [String(formData.podpisany || "").trim()]
      .filter(Boolean)
      .filter((recipient) => recipient !== loggedUsername);

    const extraRecipients = normalizeRecipients(additionalRecipients).filter(
      (recipient) => recipient !== loggedUsername,
    );

    const recipients = [...new Set([...defaultRecipients, ...extraRecipients])];
    const finalComment = recipients.length
      ? `${commentText.trim()}\n\n---\nOdbiorcy powiadomienia: ${recipients.join(", ")}`
      : commentText.trim();

    const payload = {
      id,
      comment: finalComment,
      username: loggedUsername,
      recipients,
      timestamp: new Date().toISOString(),
      mrn: formData.mrn,
      status: formData.status || "KW rozpoczęta",
      podpisany: formData.podpisany,
    };

    setIsCommentSubmitting(true);

    try {
      const endpoint = recipients.length
        ? `${BASE_URL}/kw-comments/addWithNotification`
        : `${BASE_URL}/kw-comments/${id}`;

      const response = await axios.post(endpoint, payload, {
        headers: getAuthHeaders(),
      });

      setCommentText("");
      setAdditionalRecipients([]);
      setRecipientsInputValue("");
      setRecipientsError("");
      await loadComments();

      toast.success(
        response.data?.message ||
          (recipients.length
            ? "Komentarz został zapisany i wysłano powiadomienie mailowe."
            : "Komentarz został zapisany."),
      );
    } catch (error) {
      console.error("Błąd podczas zapisu komentarza KW:", error);
      toast.error(
        error.response?.data?.message || "Nie udało się zapisać komentarza KW.",
      );
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const handleRecordMailNotification = async () => {
    const confirmed = window.confirm(
      "Pamiętaj o zapisie maila na dysku w celu poprawnej archwizacji. Czy chcesz kontynuować?",
    );

    if (!confirmed) {
      return;
    }

    try {
      const timestamp = moment()
        .tz("Europe/Warsaw")
        .format("YYYY-MM-DDTHH:mm:ss");
      const displayTimestamp = moment()
        .tz("Europe/Warsaw")
        .format("HH:mm, DD-MM-YYYY");

      const defaultRecipients = [String(formData.podpisany || "").trim()]
        .filter(Boolean)
        .filter((recipient) => recipient !== loggedUsername);

      const extraRecipients = normalizeRecipients(additionalRecipients).filter(
        (recipient) => recipient !== loggedUsername,
      );

      const recipients = [
        ...new Set([...defaultRecipients, ...extraRecipients]),
      ];
      const notificationComment = `${mailNotificationPrefix}\nOtrzymano wiadomość mailową w sprawie.\nData i godzina: ${displayTimestamp}\nKliknięte przez: ${loggedUsername}`;

      if (recipients.length > 0) {
        await axios.post(
          `${BASE_URL}/kw-comments/addWithNotification`,
          {
            id,
            comment: notificationComment,
            username: loggedUsername,
            recipients,
            timestamp,
            mrn: formData.mrn,
            status: formData.status || "KW rozpoczęta",
            podpisany: formData.podpisany,
          },
          { headers: getAuthHeaders() },
        );

        toast.success(
          "Odnotowano otrzymanie wiadomości mailowej i wysłano e-mail.",
        );
      } else {
        await axios.post(
          `${BASE_URL}/kw-comments/${id}`,
          {
            username: loggedUsername,
            comment: notificationComment,
            timestamp,
          },
          { headers: getAuthHeaders() },
        );

        toast.info(
          "Odnotowano otrzymanie wiadomości mailowej. E-mail nie został wysłany (brak odbiorcy lub próba wysyłki do siebie).",
        );
      }

      await loadComments();
    } catch (error) {
      console.error("Błąd podczas odnotowywania wiadomości mailowej:", error);
      toast.error(
        error.response?.data?.message ||
          "Nie udało się odnotować wiadomości mailowej.",
      );
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const requiredValues = [
      formData.powodWpisania,
      formData.dataSAD,
      formData.mrn,
      formData.nadawca,
      formData.odbiorca,
      formData.podpisany,
      formData.rodzajSAD,
      formData.procedura,
      formData.kontrolujacy,
    ];

    if (requiredValues.some((value) => !String(value).trim())) {
      toast.warning("Uzupełnij wszystkie wymagane pola formularza.");
      return;
    }

    if (!isValidDate(formData.dataSAD)) {
      toast.warning("Data SAD musi być w formacie dd.mm.yyyy.");
      return;
    }

    if (!isValidMrn(formData.mrn)) {
      toast.warning(
        `MRN musi zaczynać się od XXPL, gdzie XX >= ${MIN_MRN_PREFIX_YEAR} (np. 23PL, 24PL) i mieć 18-20 znaków (tylko litery i cyfry).`,
      );
      return;
    }

    if (!isValidNrAkt(formData.nrAkt)) {
      toast.warning("Jeśli podasz Nr akt, musi mieć format dane1/dane2.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildEntry(formData);
      const response = await axios.put(`${BASE_URL}/kw-dane/${id}`, payload, {
        headers: getAuthHeaders(),
      });

      if (response.data?.isDuplicate) {
        toast.warning(
          response.data?.message ||
            "Zmiany zapisano, ale MRN już istnieje i rekord oznaczono jako duplikat.",
        );
      } else {
        toast.success(
          response.data?.message || "Zmiany wpisu KW zostały zapisane.",
        );
      }

      await loadEntryDetails();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Nie udało się zaktualizować wpisu KW.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.content}>
      <div className={pageClasses.pageHeader}>
        <div>
          <h1 className={pageClasses.title}>
            <GavelIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
            Szczegóły wpisu KW
          </h1>
          <div className={pageClasses.subtitle}>Kontrola wewnętrzna</div>
        </div>
      </div>

        <Paper className={classes.paper}>
          {isLoading ? (
            <div className={classes.loadingBox}>
              <CircularProgress />
            </div>
          ) : (
            <form onSubmit={handleSave}>
              <Typography variant="h5" className={classes.sectionTitle}>
                Sekcja 1 — Edycja wpisu
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Kliknij w wybrany kafelek, aby zobaczyć wartość i edytować dane.
              </Typography>

              <div className={classes.cardsGrid}>
                {editableFields.map((field) => {
                  const locked = isFieldLocked(field.key);
                  return (
                    <Card
                      key={field.key}
                      className={`${classes.editCard} ${
                        activeField === field.key ? classes.activeCard : ""
                      } ${locked ? classes.lockedCard : ""}`}
                      onClick={() => !locked && setActiveField(field.key)}
                      title={
                        locked
                          ? field.key === "nieprawidlowosci"
                            ? "Uzupełnij listę kontrolną lub pozostaw puste Nr akt, aby odblokować to pole"
                            : field.key === "kwalifikujeDoKorekty"
                              ? "Uzupełnij pole Nieprawidłowości, aby odblokować to pole"
                              : "Uzupełnij pole Czy kwalifikuje się do korekty, aby odblokować to pole"
                          : undefined
                      }
                    >
                      <CardContent>
                        <Typography className={classes.cardLabel}>
                          {field.label}
                          {locked && " 🔒"}
                        </Typography>
                        <Typography className={classes.cardValue}>
                          {getFieldValue(field.key)}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className={classes.editorBox}>{renderActiveEditor()}</div>

              <div className={classes.actionsRow}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    isSubmitting ||
                    hasInvalidDataSAD ||
                    hasInvalidMRN ||
                    hasInvalidNrAkt
                  }
                >
                  Zapisz zmiany
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  component={RouterLink}
                  to={checklistLink || "#"}
                  onClick={(event) => {
                    if (!checklistLink) {
                      event.preventDefault();
                      handleGoToChecklist();
                      return;
                    }

                    sessionStorage.setItem(`kw-checklist-return-${id}`, "1");
                  }}
                  disabled={
                    isRedirectingToChecklist ||
                    !formData.mrn.trim() ||
                    !(formData.nrAkt?.trim() || "") ||
                    hasInvalidMRN
                  }
                >
                  {isRedirectingToChecklist
                    ? "Trwa przenoszenie..."
                    : "Przenieś do Listy kontrolnej"}
                </Button>
              </div>

              <Typography
                variant="h5"
                className={classes.sectionTitle}
                style={{ marginTop: 32 }}
              >
                Sekcja 2 — Podgląd wszystkich danych
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Poniżej widzisz pełny i czytelny podgląd wszystkich informacji o
                wpisie.
              </Typography>

              <div className={classes.summaryGrid}>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Powód wpisania
                  </Typography>
                  <Typography>{formData.powodWpisania || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Data SAD
                  </Typography>
                  <Typography>{formData.dataSAD || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>MRN</Typography>
                  <Typography>{formData.mrn || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Nr akt
                  </Typography>
                  <Typography>{formData.nrAkt || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Nadawca
                  </Typography>
                  <Typography>{formData.nadawca || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Odbiorca
                  </Typography>
                  <Typography>{formData.odbiorca || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Podpisany
                  </Typography>
                  <Typography>{formData.podpisany || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Rodzaj SAD
                  </Typography>
                  <Typography>{formData.rodzajSAD || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Procedura
                  </Typography>
                  <Typography>{formData.procedura || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Kontrolujący
                  </Typography>
                  <Typography>{formData.kontrolujacy || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Kwalifikuje do korekty
                  </Typography>
                  <Typography>
                    {formData.kwalifikujeDoKorekty || "-"}
                  </Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Nieprawidłowości
                  </Typography>
                  <Typography>{formData.nieprawidlowosci || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Rodzaj błędu
                  </Typography>
                  <Typography>{formData.rodzajBledu || "-"}</Typography>
                </div>
                <div
                  className={`${classes.summaryItem} ${
                    formData.kwalifikujeDoKorekty === "TAK"
                      ? formData.wniosekWRw === 1
                        ? classes.statusWniosekExists
                        : formData.wniosekWRw === 0
                          ? classes.statusWniosekNotExists
                          : formData.wniosekWRw === 2
                            ? classes.statusWniosekArchive
                            : classes.statusWniosekChecking
                      : ""
                  }`}
                >
                  <Typography className={classes.summaryLabel}>
                    Status wniosku w RW
                  </Typography>
                  <Typography>
                    {formData.kwalifikujeDoKorekty === "TAK"
                      ? formData.wniosekWRw === 1
                        ? "Wniosek już jest w RW"
                        : formData.wniosekWRw === 0
                          ? "Wniosku nie ma w RW"
                          : formData.wniosekWRw === 2
                            ? "Archiwum"
                            : "Sprawdzanie..."
                      : "-"}
                  </Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Ścieżka do plików
                  </Typography>
                  <Typography
                    style={{
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                    }}
                  >
                    {formData.sciezkaDoPliku || "-"}
                  </Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Data kontroli wewnętrznej
                  </Typography>
                  <Typography>
                    {formatDateTime(formData.dataKontroliWewnetrznej) || "-"}
                  </Typography>
                </div>
                {String(formData.dataUmieszczeniaWRejestrzeDs || "").trim() && (
                  <div className={classes.summaryItem}>
                    <Typography className={classes.summaryLabel}>
                      Data umieszczenia w rejestrze DS
                    </Typography>
                    <Typography>
                      {formatDateOnly(formData.dataUmieszczeniaWRejestrzeDs)}
                    </Typography>
                  </div>
                )}
                {String(formData.dataZgloszeniaDoDias || "").trim() && (
                  <div className={classes.summaryItem}>
                    <Typography className={classes.summaryLabel}>
                      Data zgłoszenia do DIAS
                    </Typography>
                    <Typography>
                      {formatDateOnly(formData.dataZgloszeniaDoDias)}
                    </Typography>
                  </div>
                )}
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Status
                  </Typography>
                  <Typography>{formData.status || "KW rozpoczęta"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Duplikat
                  </Typography>
                  <Typography>{formData.duplikat ? "TAK" : "NIE"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Utworzył
                  </Typography>
                  <Typography>{formData.createdBy || "-"}</Typography>
                </div>
                <div className={classes.summaryItem}>
                  <Typography className={classes.summaryLabel}>
                    Data utworzenia
                  </Typography>
                  <Typography>
                    {formatDateTime(formData.createdAt) || "-"}
                  </Typography>
                </div>
              </div>

              <div className={classes.commentsBox}>
                <Typography
                  variant="h5"
                  className={classes.sectionTitle}
                  style={{ marginTop: 8 }}
                >
                  Sekcja 3 — Komentarze
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Po zapisaniu komentarza powiadomienie trafi domyślnie do osoby
                  podpisanej, a opcjonalnie także do dodatkowych odbiorców.
                </Typography>

                <Button
                  variant="contained"
                  color="secondary"
                  className={classes.mailNotificationButton}
                  onClick={handleRecordMailNotification}
                >
                  Otrzymano wiadomość mailową
                </Button>

                {isCommentingBlocked && (
                  <Alert severity="info" style={{ marginTop: 16 }}>
                    Dodawanie komentarzy jest zablokowane, ponieważ wpis
                    kwalifikuje się do korekty i wymaga wpisu do Rejestru
                    Wniosków.
                  </Alert>
                )}

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  variant="outlined"
                  label={
                    formData.podpisany
                      ? `Nowy komentarz (mail domyślnie do: ${formData.podpisany})`
                      : "Nowy komentarz"
                  }
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  placeholder="Wpisz komentarz do wpisu RKW"
                  style={{ marginTop: 16 }}
                  disabled={isCommentingBlocked}
                  helperText={
                    isCommentingBlocked
                      ? "Komentarze zablokowane — wpis kwalifikuje się do korekty"
                      : ""
                  }
                />

                <div style={{ marginTop: 16 }}>
                  <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                    Dodatkowi odbiorcy powiadomienia (opcjonalnie)
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    clearOnBlur={false}
                    autoHighlight
                    selectOnFocus
                    options={validLoginsList}
                    value={additionalRecipients}
                    inputValue={recipientsInputValue}
                    onInputChange={handleRecipientsInputChange}
                    onChange={handleAdditionalRecipientsChange}
                    filterOptions={filterOptions}
                    disabled={isCommentingBlocked}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        placeholder="Wpisz lub wybierz loginy, rozdzielając je przecinkami"
                        error={Boolean(recipientsError)}
                        helperText={
                          recipientsError ||
                          "Jeśli nic nie wpiszesz, mail zostanie wysłany tylko do podpisanego."
                        }
                      />
                    )}
                  />
                </div>

                <div className={classes.actionsRow}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleSaveComment}
                    disabled={isCommentSubmitting || isCommentingBlocked}
                  >
                    {isCommentSubmitting ? "Zapisywanie..." : "Dodaj komentarz"}
                  </Button>
                </div>

                <div className={classes.commentsList}>
                  {comments.length > 0 ? (
                    comments.map((item) => (
                      <div
                        key={item.id}
                        className={
                          isMailNotificationComment(item.comment)
                            ? `${classes.commentCard} ${classes.mailNotificationComment}`
                            : classes.commentCard
                        }
                      >
                        {isMailNotificationComment(item.comment) && (
                          <Typography
                            variant="caption"
                            className={classes.mailNotificationBadge}
                          >
                            Wiadomość mailowa
                          </Typography>
                        )}
                        <div className={classes.commentMeta}>
                          <Typography variant="subtitle2">
                            Autor: {item.username || "-"}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {formatCommentDateTime(item.createdAt)}
                          </Typography>
                        </div>
                        <Typography style={{ whiteSpace: "pre-wrap" }}>
                          {getDisplayedCommentText(item.comment) || "-"}
                        </Typography>
                      </div>
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Brak komentarzy dla tego wpisu.
                    </Typography>
                  )}
                </div>
              </div>
            </form>
          )}
        </Paper>
    </div>
  );
};

export default KWDetail;
